import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { weightSubmissionsService } from '../services/apiServices';

const QK = {
  list: (status?: string) => ['weight-submissions', status ?? 'all'],
  detail: (id: number) => ['weight-submissions', id],
};

export const useWeightSubmissions = (status?: string) =>
  useQuery({
    queryKey: QK.list(status),
    queryFn: () => weightSubmissionsService.list(status ? { status } : undefined),
  });

export const useWeightSubmission = (id: number, enablePolling = false) =>
  useQuery({
    queryKey: QK.detail(id),
    queryFn: () => weightSubmissionsService.get(id),
    enabled: !!id && id > 0,
    refetchInterval: enablePolling
      ? (query) => {
          const imgs: any[] = query.state.data?.images ?? [];
          return imgs.some((i) => i.status === 'pending') ? 3000 : false;
        }
      : false,
  });

export const useExtractSubmission = (id: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => weightSubmissionsService.extract(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.detail(id) });
      qc.invalidateQueries({ queryKey: QK.list() });
    },
  });
};

export const useUpdateSubmissionImage = (submissionId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ imageId, weight }: { imageId: number; weight: number }) =>
      weightSubmissionsService.updateImage(submissionId, imageId, weight),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.detail(submissionId) }),
  });
};

export const useReanalyseSubmissionImage = (submissionId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ imageId, correctedWeightG }: { imageId: number; correctedWeightG?: number }) =>
      weightSubmissionsService.reanalyseImage(submissionId, imageId, correctedWeightG),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.detail(submissionId) }),
  });
};

export const useUpdateSubmissionWeek = (id: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (week: number) => weightSubmissionsService.updateWeek(id, week),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['weight-submissions'] });
    },
  });
};

export const useRecordSubmission = (id: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (group: 'main' | 'isolated') => weightSubmissionsService.record(id, group),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.detail(id) });
      qc.invalidateQueries({ queryKey: QK.list() });
    },
  });
};

export const useDeleteSubmission = (_statusFilter?: string | null) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => weightSubmissionsService.delete(id),
    onSuccess: (_data, id) => {
      qc.removeQueries({ queryKey: QK.detail(id) });
      qc.invalidateQueries({ queryKey: ['weight-submissions'] });
    },
  });
};
